import {ClientAnalytics} from '@shopify/hydrogen';
import {useEffect, useState, useRef} from 'react';
import {trackClickEvent} from '~/utils/trackClickEvent';

let init = false;

// Example client-side event listener
export function Analytics() {
  const [popupShowing, setPopupShowing] = useState(false);
  const [hasFired, setHasFired] = useState(false);
  const [cityName, setCityName] = useState('');
  const cityNameRef = useRef(cityName);

  let formCheckInterval;
  let popupCheckInterval;

  useEffect(() => {
    cityNameRef.current = cityName; // Update the ref after cityName changes
  }, [cityName]);

  useEffect(() => {
    if (init) return;
    init = true;

    // page view event
    ClientAnalytics.subscribe(
      ClientAnalytics.eventNames.PAGE_VIEW,
      (payload) => {
        const event = {
          event: 'page_view',
          eventCategory: 'Page',
          eventAction: 'View',
          eventLabel: 'Page Viewed',
          pageTitle: document.title,
          pageLocation: document.location.href,
          payload,
        };
        window.dataLayer.push(event);
      },
    );

    // product view event
    ClientAnalytics.subscribe(
      ClientAnalytics.eventNames.VIEWED_PRODUCT,
      (payload) => {
        var item_id =
          payload?.selectedVariant?.id.split('/').pop() ?? payload?.product?.id;
        const closestH1 = document.querySelector('h1');
        const item_list_name =
          getStoredItemListName(item_id) ?? closestH1?.textContent ?? 'NA';
        const item_list_id = generateItemListId(item_list_name);

        const product = payload.product;
        const customer = payload.shopify;
        const event = {
          event: 'view_item',
          ecommerce: {
            currency: payload.shopify.currency,
            value: product?.priceRange?.maxVariantPrice?.amount ?? 'NA',
            items: [
              {
                item_id,
                item_name: product.title, // Use Shopify variable for product name
                coupon: 'NA', // Use Shopify variable for applied coupon
                currency: payload.shopify.currency, // Use Shopify variable for currency
                discount: 0,
                item_category: product?.productType ?? 'NA', // Use Shopify variable for product main category
                item_category2: product?.collections?.edges[0]?.node?.title,
                item_category3: product?.collections?.edges[1]?.node?.title,
                item_variant:
                  payload.selectedVariant?.title ??
                  product.variants.nodes[0]?.title ??
                  '',
                price: product.priceRange.maxVariantPrice.amount,
                item_brand: product.vendor,
                item_list_name,
                item_list_id,
                index: 1, // Use Shopify variable for the position of the product displayed
                quantity: 1, // Assuming quantity is always 1 for a view_item event
              },
            ],
          },
          user_type: customer.userId ? 'existing_user' : 'new_user',
          user_status: customer.isLoggedIn ? 'loggedin' : 'guest',
          userID: customer.userId, // Use Shopify variable for unique identification of signed-in user
          customer_type: customer.userId ? 'existing_customer' : 'new_customer',
        };

        window.dataLayer.push(event);
      },
    );

    //product_click event
    ClientAnalytics.subscribe('select_item', (payload) => {
      const product = payload.product;
      const customer = payload.shopify;

      var item_id = product?.store?.variants[0]?.store?.id;

      var item_list_name = getItemListName(payload?.e?.target);
      var item_list_id = generateItemListId(item_list_name);
      // Retrieve existing item_list_names from localStorage
      var existingItemLists = JSON.parse(
        localStorage.getItem('item_list_names') || '{}',
      );

      // Update with new item_id, item_list_id, and item_list_name
      existingItemLists[item_id] = {item_list_name, item_list_id};

      // Save updated item_list_names back to localStorage
      localStorage.setItem(
        'item_list_names',
        JSON.stringify(existingItemLists),
      );

      const event = {
        event: 'select_item',
        ecommerce: {
          currency: payload.shopify.currency,
          value: product.price,
          item_list_id,
          item_list_name,
          items: [
            {
              item_id, // Use Shopify variable for SKU ID
              item_name: product.title, // Use Shopify variable for product name
              coupon: product.discount_code, // Use Shopify variable for applied coupon
              currency: payload.shopify.currency, // Use Shopify variable for currency
              discount: 0,
              item_category: product.type, // Use Shopify variable for product main category
              item_category2: product.vendor, // Use Shopify variable for product sub category1
              item_category3: 'NA',
              item_variant: payload.variant?.store.title ?? 'NA',
              price: product.price,
              brand: product.vendor,
              item_list_name,
              item_list_id,
              index: 1, // Use Shopify variable for the position of the product displayed
              quantity: 1, // Assuming quantity is always 1 for a view_item event
            },
          ],
        },
        user_type: customer.userId ? 'existing_user' : 'new_user',
        user_status: customer.isLoggedIn ? 'loggedin' : 'guest',
        userID: customer.userId, // Use Shopify variable for unique identification of signed-in user
        customer_type: customer.userId ? 'existing_customer' : 'new_customer',
      };
      window.dataLayer.push(event);
    });

    //Product List Event
    ClientAnalytics.subscribe('view_item_list', (payload) => {
      const customer = payload.shopify;
      let main_item_list_name = getItemListName(); // This function should return the main item list name for the event
      const item_list_id = generateItemListId(main_item_list_name);

      const event = {
        event: 'view_item_list',
        ecommerce: {
          item_list_id, // Unique list id for every section.
          item_list_name: main_item_list_name, //List Name of section.
          items: payload.firstEightItems.map((product, index) => {
            const item_id =
              product?.variantOverride?.store?.id ??
              product?.store?.variants[0]?.store?.id ??
              product?.store?.id;
            const item_list_name =
              getStoredItemListName(item_id) ?? getItemListName();
            const item_list_id = generateItemListId(item_list_name);

            return {
              item_id, // Use Shopify variable for SKU ID
              item_name: product.store.title, // Use Shopify variable for product name
              coupon: product.store.discount_code ?? 'NA', // Use Shopify variable for applied coupon
              currency: payload.shopify.currency, // Use Shopify variable for currency
              discount: 0,
              item_brand: product.store.vendor, // Use Shopify variable for product sub category1
              item_category: product.store.productType, // Use Shopify variable for product main category
              item_category2:
                product?.matchingShopifyProduct?.collections?.nodes[0]?.title ??
                'NA',
              item_category3:
                product?.matchingShopifyProduct?.collections?.nodes[1]?.title ??
                'NA',
              item_variant:
                product?.matchingShopifyProduct?.variants?.nodes[0]
                  ?.selectedOptions[0].value ?? 'NA',
              price: product.store.priceRange.minVariantPrice,
              item_list_id,
              item_list_name,
              index: index + 1, // Use Shopify variable for the position of the product displayed
              quantity: 1, // Assuming quantity is always 1 for a view_item event
            };
          }),
        },
        user_type: customer.userId ? 'existing_user' : 'new_user',
        user_status: customer.isLoggedIn ? 'loggedin' : 'guest',
        userID: customer.userId, // Use Shopify variable for unique identification of signed-in user
        customer_type: customer.userId ? 'existing_customer' : 'new_customer',
      };

      window.dataLayer.push(event);
    });

    // cart events
    // - view_cart
    ClientAnalytics.subscribe('view_cart', (payload) => {
      const customer = payload.shopify;
      //get item_list_name from localStorage
      const main_item_list_name = getItemListName() ?? 'NA';

      const event = {
        event: 'view_cart',
        ecommerce: {
          currency: payload.shopify.currency,
          value: payload.cost.totalAmount.amount,
          items: payload.lines.map((product, index) => {
            const item_id = product?.merchandise?.id.split('/').pop() ?? null;
            const item_list_name =
              getStoredItemListName(item_id) ?? main_item_list_name;
            const item_list_id = generateItemListId(item_list_name);
            return {
              item_id: product.merchandise.product.id.split('/').pop(),
              item_name: product.merchandise.product.title, // Use Shopify variable for product name
              coupon: product.merchandise.discount_code ?? 'NA', // Use Shopify variable for applied coupon
              currency: payload.shopify.currency, // Use Shopify variable for currency
              discount: 0,
              item_category: product.merchandise?.product?.productType ?? 'NA', // Use Shopify variable for product main category
              item_category2:
                product.merchandise?.product?.collections?.edges[0]?.node
                  ?.title ?? 'NA',
              item_category3:
                product.merchandise?.product?.collections?.edges[1]?.node
                  ?.title ?? 'NA',
              item_variant:
                product.merchandise.selectedOptions[0].value ?? 'NA',
              price: product.merchandise.priceV2.amount,
              item_list_id,
              item_list_name,
              index: index + 1, // Use Shopify variable for the position of the product displayed
              quantity: product.quantity, // Assuming quantity is always 1 for a view_item event
            };
          }),
        },
        user_type: customer.userId ? 'existing_user' : 'new_user',
        user_status: customer.isLoggedIn ? 'loggedin' : 'guest',
        userID: customer.userId, // Use Shopify variable for unique identification of signed-in user
        customer_type: customer.userId ? 'existing_customer' : 'new_customer',
      };

      window.dataLayer.push(event);
    });
    //  - add_to_cart
    ClientAnalytics.subscribe(
      ClientAnalytics.eventNames.ADD_TO_CART,
      (payload) => {
        const {cart, prevCart, addedCartLines} = payload;
        var pageViewEventData = dataLayer.filter(function (item) {
          return item.event === 'page_view';
        });
        const customer = pageViewEventData[0]?.payload?.shopify;
        const newOrChangedItems = getNewOrChangedItems(
          cart.lines.edges,
          prevCart,
        );
        const mappedItems = newOrChangedItems.map((edge, index) => {
          const node = edge.node;
          node.quantity = addedCartLines[0]?.quantity;
          var item_id = node.merchandise.id.split('/').pop();
          var item_list_name =
            getStoredItemListName(item_id) ??
            document.querySelector('h1')?.textContent;
          var item_list_id = generateItemListId(item_list_name);
          return createMappedItem(node, index, 1, item_list_id, item_list_name);
        });

        if (mappedItems.length > 0) {
          const event = {
            event: 'add_to_cart',
            ecommerce: {
              currency: cart.cost.totalAmount.currencyCode,
              value: cart.cost.totalAmount.amount,
              items: mappedItems,
            },
            user_type: customer.userId ? 'existing_user' : 'new_user',
            user_status: customer.isLoggedIn ? 'loggedin' : 'guest',
            userID: customer.userId, // Use Shopify variable for unique identification of signed-in user
            customer_type: customer.userId
              ? 'existing_customer'
              : 'new_customer',
          };
          window.dataLayer.push(event);
        }
      },
    );
    //  - remove_from_cart
    ClientAnalytics.subscribe(
      ClientAnalytics.eventNames.REMOVE_FROM_CART,
      ({cart, prevCart}) => {
        var pageViewEventData = dataLayer.filter(function (item) {
          return item.event === 'page_view';
        });
        const customer = pageViewEventData[0]?.payload?.shopify;

        const newOrChangedItems = getNewOrChangedItems(
          cart.lines.edges,
          prevCart,
        );
        const mappedItems = newOrChangedItems.map((edge, index) => {
          const newIndex = prevCart.lines.findIndex(
            (prevEdge) => prevEdge.merchandise.sku === edge.merchandise.sku,
          );
          const item_list_id = generateItemListId('Your Cart');
          return createMappedItem(
            edge,
            index,
            newIndex,
            item_list_id,
            'Your Cart',
          );
        });

        if (mappedItems.length > 0) {
          const event = {
            event: 'remove_from_cart',
            ecommerce: {
              currency: cart.cost.totalAmount.currencyCode,
              value: mappedItems[0].price,
              items: mappedItems,
            },
            user_type: customer.userId ? 'existing_user' : 'new_user',
            user_status: customer.isLoggedIn ? 'loggedin' : 'guest',
            userID: customer.userId, // Use Shopify variable for unique identification of signed-in user
            customer_type: customer.userId
              ? 'existing_customer'
              : 'new_customer',
          };
          window.dataLayer.push(event);
        }
      },
    );

    //  - update_cart
    ClientAnalytics.subscribe(
      ClientAnalytics.eventNames.UPDATE_CART,
      ({cart, prevCart}) => {
        const newOrChangedItems = getNewOrChangedItems(
          cart.lines.edges,
          prevCart,
        );
        const mappedItems = newOrChangedItems.map((edge, index) => {
          const node = edge.node;
          return createMappedItem(node, index);
        });

        if (mappedItems.length > 0) {
          const event = {
            event: 'update_cart',
            ecommerce: {
              currency: cart.cost.totalAmount.currencyCode,
              value: cart.cost.totalAmount.amount,
              items: mappedItems,
            },
          };
          window.dataLayer.push(event);
        }
      },
    );

    ClientAnalytics.subscribe('view_promotion', (payload) => {
      const customer = payload.shopify;
      const event = {
        event: 'view_promotion',
        ecommerce: {
          items: [
            {
              promotion_id: payload.promotion_id,
              promotion_name: payload.creative_name,
              creative_name: payload.creative_name,
              creative_slot: 'NA',
            },
          ],
        },
        user_type: customer?.userId ? 'existing_user' : 'new_user',
        user_status: customer?.isLoggedIn ? 'loggedin' : 'guest',
        userID: customer?.userId, // Use Shopify variable for unique identification of signed-in user
        customer_type: customer?.userId ? 'existing_customer' : 'new_customer',
      };

      window.dataLayer.push(event);
    });

    ClientAnalytics.subscribe('select_promotion', (payload) => {
      const customer = payload.shopify;
      const event = {
        event: 'select_promotion',
        ecommerce: {
          items: [
            {
              promotion_id: payload.promotion_id,
              promotion_name: payload.creative_name,
              creative_name: payload.creative_name,
              creative_slot: 'NA',
            },
          ],
        },
        user_type: customer?.userId ? 'existing_user' : 'new_user',
        user_status: customer?.isLoggedIn ? 'loggedin' : 'guest',
        userID: customer?.userId, // Use Shopify variable for unique identification of signed-in user
        customer_type: customer?.userId ? 'existing_customer' : 'new_customer',
      };

      window.dataLayer.push(event);
    });

    // Get all elements with the class '.brand-nav__item'
    let brandNavItems = document.querySelectorAll('.brand-nav__item');
    let gathrBrandClickEvent = (e) => {
      trackClickEvent('gathr_brand_click', e);
    };
    brandNavItems.forEach((item) => {
      item.addEventListener('click', gathrBrandClickEvent);
    });

    function handleFooterClick(event) {
      const footerElement = document.querySelector('.footer');
      if (
        footerElement &&
        event.target.tagName === 'A' &&
        footerElement.contains(event.target)
      ) {
        const mainColElement = event.target.closest('.footer__main-col');
        const mainHeadingElement = mainColElement.querySelector(
          '.footer__main-heading',
        );
        const headerTitle = mainHeadingElement
          ? mainHeadingElement.textContent.trim()
          : '';
        const ctaText = event.target.textContent.trim();
        const linkUrl = event.target.href;
        const eventDetails = {
          link_url: linkUrl,
          cta_text: ctaText,
          header_title: headerTitle,
        };
        trackClickEvent('footer_navigation_click', eventDetails);
        event.stopPropagation();
      }
    }
    document.addEventListener('click', handleFooterClick);

    // Function to handle click events inside the .footer__main-socials
    function handleSocialMediaElementClick(event) {
      const socialMediaElement = document.querySelector(
        '.footer__main-socials',
      );

      if (socialMediaElement?.contains(event.target)) {
        const anchor =
          event.target.tagName === 'a'
            ? event.target
            : event.target.closest('a');
        const cta_text = anchor?.textContent ?? '';
        const linkUrl = anchor?.href ?? '';
        const href = anchor?.href;
        const cleanedHref = href?.replace('www.', '') ?? '';
        const social_platform = cleanedHref.split('/')[2]?.split('.')[0] ?? '';
        const eventDetails = {
          social_platform,
          linkUrl,
          cta_text,
        };
        trackClickEvent('social_icon_interaction', eventDetails);
      }
    }
    document.addEventListener('click', handleSocialMediaElementClick);

    function handleScrollContinuous() {
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const scrollPosition = window.scrollY;
      const scrollPercentage =
        (scrollPosition / (documentHeight - windowHeight)) * 100;
      const trackingPercentages = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
      for (const percentage of trackingPercentages) {
        if (
          scrollPercentage >= percentage &&
          !trackedPercentages.includes(percentage)
        ) {
          const eventDetails = {
            scroll_percentage: `${percentage}%`,
          };
          trackClickEvent('scroll_tracking', eventDetails);
          trackedPercentages.push(percentage);
        }
      }
    }
    const trackedPercentages = [];
    window.addEventListener('scroll', handleScrollContinuous);

    // Function to handle consent_popup_interaction
    function handleConsentPopupClick(event) {
      trackClickEvent('consent_popup_interaction', event);
    }
    setTimeout(() => {
      const cookieNotificationElement = document.getElementById(
        'cookie-notification',
      );
      cookieNotificationElement?.addEventListener(
        'click',
        handleConsentPopupClick,
      );
      const cookieManagerElement = document.getElementById('modal-wrap');
      cookieManagerElement?.addEventListener('click', handleConsentPopupClick);
      const reviewWidgetElements =
        document.getElementsByClassName('yotpo-wrapper');
      for (const element of reviewWidgetElements) {
        element.addEventListener('click', handleReviewClick);
      }

      // Search all iframes for vimdeo embeds
      var iframes = document.querySelectorAll('iframe');

      for (var i = 0; i < iframes.length; i++) {
        var iframe = iframes[i];
        var src = iframe.src;

        if (/player\.vimeo\.com\/video\/\d+/i.test(src)) {
          var vimeoSdkScript = document.createElement('script');

          vimeoSdkScript.src = 'https://player.vimeo.com/api/player.js';

          vimeoSdkScript.onload = function () {
            var players = [];
            iframes.forEach(function (iframe) {
              // eslint-disable-next-line no-undef
              var player = new Vimeo.Player(iframe);
              players.push(player);

              player.on('play', function (data) {
                var percentage = (data.percent * 100).toFixed(0);
                player.getVideoTitle().then(function (title) {
                  const eventDetails = {
                    video_title: title,
                    video_status: 'start',
                    video_percentage: percentage + '%',
                  };
                  trackClickEvent('video_interaction', eventDetails);
                });
              });

              player.on('pause', function (data) {
                var percentage = (data.percent * 100).toFixed(0);
                player.getVideoTitle().then(function (title) {
                  const eventDetails = {
                    video_title: title,
                    video_status: 'pause',
                    video_percentage: percentage + '%',
                  };
                  trackClickEvent('video_interaction', eventDetails);
                });
              });

              player.on('ended', function (data) {
                var percentage = (data.percent * 100).toFixed(0);
                player.getVideoTitle().then(function (title) {
                  const eventDetails = {
                    video_title: title,
                    video_status: 'complete',
                    video_percentage: percentage + '%',
                  };
                  trackClickEvent('video_interaction', eventDetails);
                });
              });

              player.on('timeupdate', function (data) {
                var percentage = (data.percent * 100).toFixed(0);
                if (percentage % 10 === 0) {
                  player.getVideoTitle().then(function (title) {
                    const eventDetails = {
                      video_title: title,
                      video_status: 'play',
                      video_percentage: percentage + '%',
                    };
                    trackClickEvent('video_interaction', eventDetails);
                  });
                }
              });
            });
          };
          document.head.appendChild(vimeoSdkScript);
          break;
        }
      }
    }, 1000);

    // Function to handle review_clicks
    function handleReviewClick(event) {
      var eventType = '';
      const dataType = event.target.getAttribute('data-type');
      if (typeof dataType !== 'undefined' && dataType !== null) {
        switch (dataType) {
          case 'reviews':
            eventType = 'write_a_review_click';
            break;
          case 'questions':
            eventType = 'ask_a_question_click';
            break;
        }
        trackClickEvent(eventType);
      }
    }

    const heroElements = document.querySelectorAll('.hero');
    const btnElements = [];
    heroElements.forEach((heroElement) => {
      const btnsInHero = heroElement.querySelectorAll('.btn');
      btnElements.push(...btnsInHero);
    });
    btnElements.forEach((btnElement) => {
      btnElement.addEventListener('click', function () {
        const parentHero = this.closest('.hero');
        var headerTitle = '';
        if (parentHero) {
          const heroTitleElement = parentHero.querySelector('.hero__title');
          if (heroTitleElement) {
            headerTitle = heroTitleElement.textContent;
          }
        }

        const eventDetails = {
          header_title: headerTitle,
          cta_text: this.textContent,
        };
        trackClickEvent('button_click', eventDetails);
      });
    });

    document.addEventListener('click', function (event) {
      if (event.target.classList.contains('readmore-link')) {
        const buttonText = event.target.textContent;
        const productTitle = document.querySelector(
          '.product-detail__title',
        ).textContent;
        const eventDetails = {
          header_title: productTitle,
          cta_text: buttonText,
        };
        trackClickEvent('button_click', eventDetails);
      }
    });

    document
      .querySelectorAll('.primary-nav__list-item')
      .forEach(function (menuItem) {
        menuItem.addEventListener('click', function (event) {
          var main_category = menuItem.querySelector('h5 a')
            ? menuItem.querySelector('h5 a').textContent
            : menuItem.querySelector('h5')
            ? menuItem.querySelector('h5').textContent
            : 'NA';

          var sub_category = 'NA';
          if (event.target.classList.contains('submenu__link')) {
            sub_category = event.target.textContent;
          }

          const eventDetails = {
            main_category,
            sub_category,
          };
          trackClickEvent('top_navigation_click', eventDetails);
        });
      });

    // The popup is injected into the DOM after the page loads, so we need to check for it
    const checkPopup = () => {
      const popupElement = document.querySelector('div[data-testid="POPUP"]');

      if (popupElement) {
        clearInterval(popupCheckInterval);
        // Function to get the name of the klaviyo popup banner
        const getKlaviyoPopupBannerName = () => {
          const formElement = popupElement.querySelector('form');
          // Get the value of the data attribute
          const dataAttributeValue = formElement.getAttribute('data-testid');

          // Check if the data attribute is present and not null
          if (dataAttributeValue !== null) {
            return dataAttributeValue;
            // Now you can use dataAttributeValue as a string
          } else {
            return 'Banner Name Not Found';
          }
        };

        // Function to handle klaviyo pop up banner view
        const handleKlaviyoPopupBannerView = () => {
          const banner_name = getKlaviyoPopupBannerName();
          const eventDetails = {
            banner_name,
          };
          trackClickEvent('popup_banner_view', eventDetails);
        };

        // Function to handle klaviyo close button clicks
        const handleKlaviyoCloseFormClick = (event) => {
          const banner_name = getKlaviyoPopupBannerName();
          const eventDetails = {
            banner_name,
            cta_click: event.target.textContent || 'close_icon',
          };
          trackClickEvent('popup_banner_close', eventDetails);

          // Disconnect the IntersectionObserver when the popup is closed
          popupIntersectionObserver.disconnect();

          // Clear the interval when the popup is closed
          clearInterval(popupCheckInterval);
        };

        // Function to handle klaviyo cta button clicks
        const handleKlaviyoCTAButtonClick = (event) => {
          const banner_name = getKlaviyoPopupBannerName();
          const eventDetails = {
            banner_name,
            cta_click: event.target.textContent || 'NA',
          };
          const emailInput = document.getElementById('email_97929443');
          const emailValue = emailInput.value.trim();

          // Check if the email value is not empty and matches the email pattern
          if (
            emailValue !== '' &&
            emailValue.trim() !== '' &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue.trim())
          ) {
            trackClickEvent('popup_banner_cta_click', eventDetails);
          }
        };

        var popupIntersectionObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!popupShowing && !hasFired) {
                  handleKlaviyoPopupBannerView();
                  setPopupShowing(true);
                  setHasFired(true);
                } else {
                  setPopupShowing(false);
                  setHasFired(false);
                }
              }
            });
          },
          {threshold: 1},
        ); // threshold defines how much of the item should be visible, 1 means 100%
        popupIntersectionObserver.observe(popupElement);
        var buttons = popupElement.querySelectorAll('button');
        var closeButton, ctaButton, noThanksButton;
        // Assuming the close button is always the first button
        closeButton = buttons.length >= 1 ? buttons[0] : null;
        buttons.forEach((button) => {
          const buttonText = button.textContent.trim();

          // Check for the CTA button based on its text
          if (
            buttonText === 'Unlock Your Discount' ||
            buttonText === 'SIGN ME UP!' ||
            buttonText === 'Sign up'
          ) {
            ctaButton = button;
          }
          // Check for the No Thanks button based on its text
          else if (buttonText === 'No, thanks' || buttonText === 'NO, THANKS') {
            noThanksButton = button;
          }
        });

        if (closeButton) {
          closeButton.addEventListener('click', handleKlaviyoCloseFormClick);
        }
        if (ctaButton) {
          ctaButton.addEventListener('click', handleKlaviyoCTAButtonClick);
        }
        if (noThanksButton) {
          noThanksButton.addEventListener('click', handleKlaviyoCloseFormClick);
        }
      }
    };

    popupCheckInterval = setInterval(checkPopup, 500);

    function formSubscribe() {
      try {
        document
          .querySelectorAll('form.klaviyo-form button[type="button"]')
          .forEach((button) => {
            if (
              button.textContent.trim() === 'Subscribe' &&
              !button.getAttribute('data-listener-attached')
            ) {
              button.addEventListener('click', (event) => {
                const emailInput = document.getElementById('email_84222989');
                const emailValue = emailInput.value.trim();

                if (
                  emailValue !== '' &&
                  emailValue.trim() !== '' &&
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue.trim())
                ) {
                  trackClickEvent('subscribed_click', event);
                }
              });
              button.setAttribute('data-listener-attached', 'true');
            }
          });
      } catch (error) {
        console.error('Error in formSubscribe:', error);
      }
    }

    formCheckInterval = setInterval(formSubscribe, 500);

    // Function to handle locally plugin location change
    const handleChangeLocationClick = (selected_location) => {
      const eventDetails = {
        selected_location,
        cta_text: 'change',
      };
      trackClickEvent('change_location_click', eventDetails);
    };

    document.addEventListener('click', function (event) {
      if (event.target.classList.contains('lcly-location-prompt-link')) {
        const cityElement = document.querySelector('.lcly-city-name');
        if (cityElement) {
          const currentCityName = cityElement.textContent.trim();
          if (currentCityName !== cityNameRef.current) {
            setCityName(currentCityName);
            handleChangeLocationClick(currentCityName);
          }
        }
      }
    });

    formCheckInterval = setInterval(formSubscribe, 500);
  }, [popupShowing, hasFired]);
  useEffect(() => {
    const handleClick = (event) => {
      // Find the closest ancestor of the clicked element (or the element itself) that has the specified class
      const promotionElement = event.target.closest(
        '.promotion-tracking-enabled',
      );

      if (promotionElement) {
        // Retrieve the creative name from the data attribute
        const creativeName = promotionElement.getAttribute(
          'data-promotion-creative-name',
        );
        ClientAnalytics.publish('select_promotion', true, {
          creative_name: creativeName,
        });
      }
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return null;
}

function getNewOrChangedItems(lines, prevCart) {
  const notExistsInNewCart = prevCart?.lines?.filter((prevEdge) => {
    if (!prevEdge || !prevEdge.merchandise) {
      return false; // Skip this entry if it's not valid
    }

    const prevSku = prevEdge.merchandise.sku;

    // Check if the item still exists in the new cart
    const existsInNewCart = lines.some((currentEdge) => {
      const currentSku = currentEdge?.node?.merchandise?.sku;
      return currentSku === prevSku;
    });

    // If the item doesn't exist in the new cart, include it as removed
    if (!existsInNewCart) return prevEdge;
  });

  if (notExistsInNewCart?.length > 0) {
    return notExistsInNewCart;
  }

  return lines.filter((edge) => {
    const newNode = edge?.node;
    const prevNode = prevCart?.lines?.find((prevEdge) => {
      if (!prevEdge || !prevEdge.merchandise) {
        return false; // Skip this entry if it's not valid
      }
      return prevEdge?.merchandise?.sku === newNode.merchandise.sku;
    });
    if (!prevNode || prevNode.quantity !== newNode.quantity) {
      return true; // Item is new or quantity has changed
    }
    return false;
  });
}

function createMappedItem(
  node,
  index = 0,
  newIndex = 1,
  item_list_id = 'NA',
  item_list_name = 'NA',
) {
  return {
    item_id: node.merchandise.product.id.split('/').pop(),
    item_name: node.merchandise.product.title,
    coupon: node.merchandise.product.discount_code ?? 'NA',
    currency: node.cost.totalAmount.currencyCode,
    discount: 0,
    index: newIndex,
    item_brand: node.merchandise.product.vendor,
    item_category: node.merchandise.product.productType,
    item_category2: 'NA',
    item_category3: 'NA',
    item_category4: 'NA',
    item_category5: 'NA',
    item_list_id,
    item_list_name,
    item_variant: node.merchandise.selectedOptions[0].value ?? '',
    price: parseFloat(node.merchandise.priceV2.amount),
    quantity: node.quantity,
  };
}

function fetchAndFormatMasterDetail() {
  // Initialize an array to hold the formatted strings for each category
  const formattedStrings = [];

  // Find all filter group divs
  const filterGroups = document.querySelectorAll(
    '.searchspring-facet-filters > div',
  );

  filterGroups.forEach((group) => {
    // Extract the category name
    const categoryElement = group.querySelector(
      '.searchspring-facet-filters__heading',
    );
    if (categoryElement) {
      const categoryName = categoryElement.textContent.trim();

      // Extract all options within this category
      const options = group.querySelectorAll(
        '.searchspring-facet-filters__option',
      );
      const optionValues = Array.from(options).map((option) => {
        // Split to remove the count and trim to clean up the string
        return option.textContent.trim().split(' (')[0];
      });

      if (optionValues.length) {
        // Format the string for this category
        const formattedString = `${categoryName} - ${optionValues.join(' | ')}`;
        formattedStrings.push(formattedString);
      }
    }
  });

  // Join all formatted strings for each category
  return formattedStrings.join(' | ');
}

function getItemListName(targetElement) {
  var item_list_name = fetchAndFormatMasterDetail();

  if (!item_list_name) {
    // Prioritize getting "You May Also Like" text from the specific structure
    var moduleTitle = document.querySelector('.page-header h1');
    if (moduleTitle) {
      item_list_name = moduleTitle.textContent.trim();
    } else {
      // Look for sibling h1 or h2 elements
      var siblingHeading =
        targetElement?.previousElementSibling ||
        targetElement?.nextElementSibling;
      while (
        siblingHeading &&
        siblingHeading.tagName !== 'H1' &&
        siblingHeading.tagName !== 'H2'
      ) {
        siblingHeading =
          siblingHeading?.previousElementSibling ||
          siblingHeading?.nextElementSibling;
      }

      // If a sibling h1 or h2 is found, use its text content
      if (
        siblingHeading &&
        (siblingHeading.tagName === 'H1' || siblingHeading.tagName === 'H2')
      ) {
        item_list_name = siblingHeading.textContent.trim();
      } else {
        // If no sibling h1 or h2 is found, then look for descendant h1 or h2
        var h1Descendant = targetElement?.querySelector('h1');
        var h2Descendant = !h1Descendant
          ? targetElement?.querySelector('h2')
          : null;

        if (h1Descendant || h2Descendant) {
          item_list_name = (h1Descendant || h2Descendant).textContent.trim();
        } else {
          // get first h1 or h2
          var firstHeading = document.querySelector('h1, h2');
          if (firstHeading) {
            item_list_name = firstHeading.textContent.trim();
          } else {
            item_list_name = 'NA';
          }
        }
      }
    }
  }
  // if item_list_name has this pattern Your Cart (some number) remove everything in the parenthesis and the parenthesis
  if (item_list_name.includes('(')) {
    item_list_name = item_list_name.replace(/\(.*?\)/g, '').trim();
  }
  item_list_name = item_list_name.substring(0, 100);
  return item_list_name;
}
function generateItemListId(item_list_name) {
  return item_list_name
    .split(' ')
    .join('_')
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, '_');
}

function getStoredItemListName(item_id) {
  // Retrieve existing item_list_names from localStorage
  var existingItemLists = JSON.parse(
    localStorage.getItem('item_list_names') || '{}',
  );

  // Attempt to find a matching item_list_name in localStorage
  const storedItem = existingItemLists[item_id];
  return storedItem ? storedItem.item_list_name : null;
}
